import React from "react";
import { InputAdornment, IconButton, Icon } from "@material-ui/core";
import { get, debounce } from "lodash";
import { NoteIcon } from "helpers/icon-finder";
import BaseTextInput from "./base-text-input";
import BaseTextField from "./components/base-text-field";

export function TextInput({
  field: { name, value },
  form: { touched, errors, setFieldValue, setFieldTouched },
  suffix,
  onChange,
  onBlur,
  ...other
}) {
  const isTouched = get(touched, name, false);

  const handleTouched = () => {
    if (isTouched) return;
    setFieldTouched(name, true);
    if (onBlur) {
      onBlur();
    }
  };

  return (
    <BaseTextInput
      name={name}
      touched={isTouched}
      error={get(errors, name, null)}
      value={value ?? ""}
      onChange={e => {
        setFieldValue(name, e.target.value);
        if (onChange) {
          onChange(e.target.value, setFieldValue);
        }
      }}
      onBlur={handleTouched}
      endAdornment={suffix && <InputAdornment position="end" className="px-8">{suffix}</InputAdornment>}
      {...other}
    />
  );
}

export function TextInputField({
  field: { name, value },
  form: { touched, errors, setFieldValue, setFieldTouched, validateForm },
  suffix,
  onChange,
  onBlur,
  ...other
}) {
  const isTouched = get(touched, name, false);

  // Use ref to mark field as touched after first change when using default validation
  const hasChangedRef = React.useRef(false);

  // Debounced validation to reduce lag
  const debouncedValidateRef = React.useRef();

  if (!debouncedValidateRef.current) {
    debouncedValidateRef.current = debounce(() => {
      validateForm();
    }, 100);
  }

  // Cleanup on unmount
  React.useEffect(() => () => {
    if (debouncedValidateRef.current) {
      debouncedValidateRef.current.cancel();
    }
  }, []);

  const handleTouched = event => {
    // Cancel pending validation and validate immediately on blur
    if (debouncedValidateRef.current) {
      debouncedValidateRef.current.cancel();
    }
    setFieldTouched(name, true);
    validateForm();
    if (onBlur) {
      onBlur(event);
    }
  };

  const handleChange = val => {
    // Update value immediately (no lag in display)
    setFieldValue(name, val, false);

    if (onChange) {
      onChange(val, setFieldValue);
    } else {
      // For default behavior, mark as touched after first change
      if (!hasChangedRef.current) {
        hasChangedRef.current = true;
        setFieldTouched(name, true, false);
      }
      // Debounce validation to avoid lag while typing
      debouncedValidateRef.current();
    }
  };

  return (
    <BaseTextField
      name={name}
      touched={isTouched || hasChangedRef.current}
      error={get(errors, name, null)}
      value={value ?? ""}
      onChange={e => handleChange(e.target.value)}
      onBlur={handleTouched}
      endAdornment={suffix ? <InputAdornment position="end" className="px-8">{suffix}</InputAdornment> : null}
      {...other}
    />
  );
}

export default TextInputField;

export const NumberInput = props => {
  const { negative = false } = props;
  return (
    <TextInputField
      {...props}
      type="number"
      onKeyDown={e => (negative ? (e.key === "+" || e.key === "e") : (e.key === "-" || e.key === "+" || e.key === "e")) && e.preventDefault()}
    />
  );
};

export const TextAreaInput = props => (
  <TextInputField
    {...props}
    rows={props.rows || "4"}
    multiline
  />
);

export const NoteInput = props => (
  <TextAreaInput
    icon={<NoteIcon />}
    {...props}
  />
);

export const CurrencyInput = props => {
  const { form: { setFieldValue }, field: { name, value }, loading } = props;
  return (
    <TextInputField
      loading={loading}
      type="number"
      defaultActive
      onKeyDown={e => (e.key === "-" || e.key === "+" || e.key === "e") && e.preventDefault()}
      suffix={value && <IconButton edge="end" onClick={() => setFieldValue(name, null)}><Icon fontSize="small">clear</Icon></IconButton>}
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
      {...props}
    />
  );
};

export const PasswordInput = props => {
  const [showPassword, setShowPassword] = React.useState(false);
  const type = showPassword ? "text" : "password";

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowPassword(false);
    }, 6000);
    return () => clearTimeout(timer);
  }, [showPassword]);

  return (
    <TextInputField
      type={type}
      inputProps={{
        autoComplete: "current-password",
      }}
      suffix={(
        <IconButton
          edge="end"
          onClick={() => setShowPassword(!showPassword)}
        >
          <Icon fontSize="small">{showPassword ? "visibility" : "visibility_off"}</Icon>
        </IconButton>
      )}
      {...props}
    />
  );
};
